import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import type { IUserRoleModal } from "@app/dashboard/types/innerLayout/user_management/modals/user-role";
import { getRoles } from "@app/dashboard/services/inner_layout/user_management/roles/get-roles";

const useGetRolesList = ({
  type,
  id,
  enabled = true,
  lang
}: {
  type: IUserRoleModal['type'] | 'filters',
  id?: number,
  enabled?: boolean,
  lang?: 'en' | 'ar'
}): {
  rolesData: {
    label: string,
    id: string,
    description: string
  }[],
  roles: {
    label: string,
    id: string,
    description: string,
    selected: boolean
  }[],
  isLoading: boolean,
  isError: boolean,
  isRefetching: boolean,
} => {
  const changeRoleOnlyType = type === 'changeRole' || type === 'updateExistingRole';

  const queryFnParams = changeRoleOnlyType ?
    { id }
    :
    type === 'filters' ?
      { forFilter: true }
      :
      {};

  // Create consistent query key based on actual parameters
  const queryKey = changeRoleOnlyType ?
    [QUERY_KEYS.GET_ROLES_FROM_ID]
    :
    type === 'filters' ?
      [QUERY_KEYS.GET_FILTER_ROLES]
      :
      [QUERY_KEYS.GET_ROLES];

  const {
    isLoading,
    isError,
    isRefetching,
    data: rolesResponse
  } = useQuery({
    queryKey: lang ? [...queryKey, lang] : queryKey,
    queryFn: () => getRoles(queryFnParams).then(res => res.data.data),
    enabled: enabled
  });

  const rolesData = useMemo(() => rolesResponse ?
    (
      type === 'filters' ?
        rolesResponse.roles
        :
        [
          ...rolesResponse.system_roles,
          ...rolesResponse.custom_roles
        ]
    )
    :
    [], [rolesResponse, type]);

  const roles = useMemo(() => rolesData?.map((role: any) => ({
    label: role.title,
    id: role.id,
    description: role.sub_title,
    selected: role.selected
  })) || [], [rolesData]);

  return { rolesData, roles, isLoading, isError, isRefetching };
};

export default useGetRolesList;
